import React, { useState } from "react";
import {
  Box,
  Divider,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { TabContext, TabPanel } from "@mui/lab";
import { Comments, DataLayout } from "../../../../shared/components";
import { useFetchMeetingsDetails } from "../../../meetingManager/hooks/useFetchMeetingsDetails";
import { Grid } from "@mui/system";
import { useFetchTask } from "../../hooks/useFetchTask";

interface ContractorMeetingDrawerProps {
  meetingId: number;
  taskId?: number;
  onCompleteTask?: (taskId: string) => Promise<void>;
  isAssignedToUser?: boolean;
  refetch?: () => void;
  onRefetchRequired?: (refetchFn: () => void) => void;
}

export const ContractorMeetingDrawer: React.FC<
  ContractorMeetingDrawerProps
> = ({ meetingId, taskId, onCompleteTask, isAssignedToUser = false, refetch, onRefetchRequired }) => {
  const { meetingDetails, refetch: refetchMeetingDetails } =
    useFetchMeetingsDetails({
      meetingId: String(meetingId),
    });

  const { taskData, refetch: refetchTaskData } = useFetchTask({
    object_type: ["meeting"],
    object_id: meetingId,
    my_tasks: true,
  });

  // Combined refetch function for both meeting and task data
  const handleRefetchData = async () => {
    await Promise.all([
      refetchMeetingDetails(),
      refetchTaskData(),
    ]);
  };

  // Notify parent about the refetch function when component mounts or dependencies change
  React.useEffect(() => {
    if (onRefetchRequired) {
      onRefetchRequired(handleRefetchData);
    }
  }, [meetingId, taskId, onRefetchRequired]);

  const filteredTaskData = taskData?.filter(
    (task: any) => task.task_id === taskId,
  )[0];

  const [currentTab, setCurrentTab] = useState("overview");
  const [isCompleting, setIsCompleting] = useState(false);

  const handleChangeTab = (_e: React.SyntheticEvent, tab: string) => {
    setCurrentTab(tab);
  };

  const handleCompleteAction = async () => {
    if (onCompleteTask) {
      setIsCompleting(true);
      try {
        await onCompleteTask(String(meetingId));
        // Refetch task data after completing the meeting task
        if (refetch) {
          refetch();
        }
      } finally {
        setIsCompleting(false);
      }
    }
  };

  return (
    <Box height="100%" display="flex" flexDirection="column">
      <TabContext value={currentTab}>
        <Tabs
          value={currentTab}
          onChange={handleChangeTab}
          aria-label="Tabs"
          sx={{ minHeight: "auto" }}
        >
          <Tab
            sx={{ height: "auto", minHeight: "auto" }}
            label="Overview"
            value="overview"
          />
          {/* <Tab sx={{ height: 'auto', minHeight: 'auto' }} label="Comments" value="comments" /> */}
        </Tabs>
        <Divider />
        <TabPanel
          value="overview"
          sx={{
            p: 2,
            height: "calc(100% - 50px)",
            overflowY: "auto",
            flex: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <Box display="flex" flexDirection="column" gap={2} flex={1}>
            <DataLayout title="Meeting Overview">
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
              >
                {[
                  { label: "Meeting ID", value: meetingDetails?.id },
                  { label: "Title", value: meetingDetails?.title },
                  {
                    label: "Category",
                    value: meetingDetails?.category_display_text,
                  },
                ].map((item, index) => (
                  <Grid key={index} size={12} container>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.primary">
                        {item.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </DataLayout>
            <DataLayout title="Task Details">
              <Box
                sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}
              >
                {[
                  { label: "Task ID", value: filteredTaskData?.task_id },
                  { label: "Project", value: filteredTaskData?.project_title },
                  { label: "Title", value: filteredTaskData?.task_name },
                  {
                    label: "Category",
                    value: filteredTaskData?.category_display_text,
                  },
                  {
                    label: "Status",
                    value: filteredTaskData?.status_display_text,
                  },
                  {
                    label: "Priority",
                    value: filteredTaskData?.priority_display_text,
                  },
                  { label: "Assignee", value: filteredTaskData?.owner },
                ].map((item, index) => (
                  <Grid key={index} size={12} container>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.secondary">
                        {item.label}
                      </Typography>
                    </Grid>
                    <Grid size={6}>
                      <Typography variant="body2" color="text.primary">
                        {item.value}
                      </Typography>
                    </Grid>
                  </Grid>
                ))}
              </Box>
            </DataLayout>

            {/* 
            <Stack direction="row" justifyContent="flex-end" gap={1}>
              <Button
                variant="contained"
                onClick={handleCompleteAction}
                disabled={isCompleting}
              >
                {isCompleting ? "Completing..." : "Complete Action"}
              </Button>
            </Stack> 
            */}
          </Box>
        </TabPanel>
        <TabPanel
          value="comments"
          sx={{ p: 2, height: "calc(100% - 50px)", overflowY: "auto", flex: 1 }}
        >
          <Comments
            topic={["MEETING_COMMENT_ADDED"]}
            sub_event={["MEETING_MANAGER"]}
            object_id={String(meetingId)}
            parent_id=""
          />
        </TabPanel>
      </TabContext>
    </Box>
  );
};
